import { Vec3 } from '@/core/math/Vec3';
import { CelestialBody } from '@/universe/CelestialBody';
import { BIOMES, BiomeDef } from './BiomeDef';
import { BIOME_VEGETATION, UNDERWATER_VEGETATION } from './VegetationDef';
import { ChunkBuildRequest, ChunkGeometryData, PaletteColors, releaseChunkMeshArrays } from './ChunkBuilder';
import {
  ChunkId,
  chunkChildren,
  chunkExtent,
  chunkKey,
  chunkSizeMetres,
  distanceToChunk,
  isBeyondHorizon,
  maxLevelFor,
  rootChunks,
} from './CubeSphere';
import { HeightField } from './HeightField';
import { BiomeBands, TerrainParams, calibrateContinents, terrainParamsFor } from './TerrainPipeline';
import { vegetationCellsPerFaceEdge, vegetationCellStride } from './VegetationScatter';
import { poolJobKey, WorkerPool } from './WorkerPool';
import { TerrainProfile } from '@/universe/BodyDef';

export interface TerrainQualitySettings {
  /** Vertices per chunk edge. */
  resolution: number;
  /**
   * Subdivide when the camera is closer than `splitFactor * chunkSize`.
   * Larger values mean more detail and more chunks.
   */
  splitFactor: number;
  /**
   * Merge when further than `mergeFactor * chunkSize`. Must exceed splitFactor;
   * the gap between them is the LOD hysteresis that prevents oscillation.
   */
  mergeFactor: number;
  /** Maximum resident chunks before the least relevant are evicted. */
  maxChunks: number;
  /**
   * Maximum outstanding build requests.
   *
   * This is a queue budget, not a concurrency limit: the worker pool already
   * runs one job per worker and picks the best-priority job from its queue. The
   * budget only has to be generous enough that the pool always has the *right*
   * candidates to choose from. Setting it near the worker count instead starves
   * the descent, because the tree is walked roots-first and distant chunks claim
   * the slots before the near ones are even considered.
   */
  maxPendingBuilds: number;
  /** Target vertex spacing at the finest level, metres. */
  targetVertexSpacing: number;
}

export const DEFAULT_TERRAIN_QUALITY: TerrainQualitySettings = {
  resolution: 64,
  splitFactor: 1.9,
  mergeFactor: 2.3,
  maxChunks: 1200,
  maxPendingBuilds: 64,
  targetVertexSpacing: 3,
};

export interface VegetationQualitySettings {
  /** Master switch; also false whenever the quality tier's scatter budget is 0. */
  enabled: boolean;
  /**
   * Target side of one scatter cell, metres — the density knob.
   *
   * This, not an instance cap, is what a quality tier turns: the scatter grid
   * belongs to the planet rather than to a chunk (see `VegetationScatter.ts`),
   * so halving the cell size quadruples the plants at *every* LOD level at
   * once and keeps them in the same places. An instance cap cannot do that;
   * it thins per chunk, which is per level, which is the coupling that made
   * approaching a forest re-roll it.
   */
  cellMetres: number;
  /**
   * Multiplier on every biome's authored plants-per-km² — the count knob.
   *
   * Separate from `cellMetres` because the two do genuinely different things:
   * halving the cell size quarters each cell's presence probability while
   * quadrupling the cells, leaving the plant count alone and only letting
   * neighbours stand closer together. Cell size buys spacing fidelity (and
   * stops a very dense biome from clamping to one plant per cell, which reads
   * as a lattice); this buys plants.
   */
  densityScale: number;
  /** Per-chunk allocation ceiling. A safety net against a pathological biome, not a density knob. */
  maxInstancesPerChunk: number;
}

/**
 * How many levels below `maxLevel` still qualify for vegetation. See its use
 * in `requestBuild`, and `vegetationRange` for what it costs the renderer.
 *
 * Three, not two, because the render side fades distant cards out over a band
 * that has to *fit inside* the region vegetation is generated for. At two the
 * coarsest vegetated level merged away closer than the fade even began, so the
 * forest ended at a chunk boundary with its billboards still at full opacity —
 * the straight-edged rectangular forest border, which is a generation extent
 * and not a draw distance.
 */
const VEGETATION_LOD_DEPTH = 3;

/**
 * Smallest value `chunkSizeMetres`' cube-to-sphere distortion correction can
 * take: `1/|(u,v,1)|` at a face corner, where `|u| = |v| = 1`. See
 * `vegetationRange`, which needs the worst case rather than a typical one.
 */
const CUBE_FACE_MIN_DISTORTION = 1 / Math.sqrt(3);

export const DEFAULT_VEGETATION_QUALITY: VegetationQualitySettings = {
  enabled: true,
  cellMetres: 5,
  densityScale: 1,
  maxInstancesPerChunk: 2400,
};

export interface ChunkRecord {
  id: ChunkId;
  key: string;
  data: ChunkGeometryData | null;
  /** True while a build is in flight. */
  pending: boolean;
  /** True when this node has been subdivided and its children should render. */
  split: boolean;
  /** Distance from the camera at the last update, metres. */
  distance: number;
  /** Simulation time of the last update this chunk was relevant. */
  lastSeen: number;
  /**
   * Index of the last LOD update that touched this chunk.
   *
   * Deliberately not the simulation time: with the game paused, or during a
   * physics warp where several updates share a timestamp, a time-based test
   * would declare the entire live tree stale and evict it.
   */
  lastTouch: number;
  size: number;
}

export interface SurfaceEvents {
  onChunkReady: (record: ChunkRecord) => void;
  onChunkRemoved: (key: string) => void;
}

export interface StreamingStats {
  resident: number;
  pending: number;
  visible: number;
  byLevel: Map<number, number>;
  evictions: number;
  maxLevelReached: number;
}

/**
 * Streaming quadtree terrain for one body.
 *
 * The tree is walked from the six cube roots every update. A node subdivides
 * when the camera is close relative to the node's surface size and merges when
 * it is far, with a deliberate gap between the two thresholds:
 *
 *     split at   distance < splitFactor * chunkSize
 *     merge at   distance > mergeFactor * chunkSize
 *
 * That gap is the hysteresis band. Without it, a camera sitting exactly on a
 * threshold subdivides and merges the same node every frame, which is both a
 * visible flicker and an unbounded stream of worker jobs.
 *
 * A parent is only hidden once all four children have geometry. That single
 * rule removes the entire class of "hole appears while a chunk loads" bugs
 * without needing a loading screen or a fade.
 *
 * The corollary is that eviction must never touch a node the tree is currently
 * using — including the *interior* nodes, which are not drawn and so are not in
 * the visible set. Evicting one of those makes its parent see an incomplete
 * child set on the next update, which collapses the whole subtree back to a
 * coarse chunk, which frees more chunks, which collapses more subtrees. The
 * symptom is terrain that flickers between full detail and a handful of huge
 * chunks several times a second while the workers never stop. Hence the
 * `lastTouch` test below: only nodes the last update did not visit may go.
 */
export class PlanetSurface {
  readonly body: CelestialBody;
  readonly params: TerrainParams;
  readonly heightField: HeightField;
  readonly maxLevel: number;
  quality: TerrainQualitySettings;
  vegetationQuality: VegetationQualitySettings;

  private chunks = new Map<string, ChunkRecord>();
  private visibleKeys = new Set<string>();
  private updateIndex = 0;
  private evictions = 0;
  private maxLevelReached = 0;
  private readonly biomeBands: [string, BiomeBands][];
  private readonly biomeColors: [string, { color: [number, number, number]; detailColor: [number, number, number] }][];
  private readonly palette: PaletteColors;
  private readonly scratch = new Vec3();
  /**
   * Whether any biome/water this body permits scatters *anything* over the
   * vegetation cell grid — living vegetation, or (for barren biomes with no
   * vegetationDensity) the rock/crystal species scattered via scatterDensity
   * instead. Same machinery either way; see VegetationScatter's density pick.
   */
  readonly hasVegetation: boolean;

  constructor(
    body: CelestialBody,
    systemSeed: number,
    private readonly pool: WorkerPool,
    private readonly events: SurfaceEvents,
    quality: TerrainQualitySettings = DEFAULT_TERRAIN_QUALITY,
    vegetationQuality: VegetationQualitySettings = DEFAULT_VEGETATION_QUALITY,
  ) {
    if (!body.def.terrain) {
      throw new Error(`Body "${body.id}" has no terrain profile and cannot have a surface`);
    }
    this.body = body;
    this.quality = { ...quality };
    this.vegetationQuality = { ...vegetationQuality };
    this.params = terrainParamsFor(
      body.id,
      systemSeed,
      body.radius,
      body.def.terrain,
      body.def.thermal,
      body.def.biomes,
    );
    this.heightField = new HeightField(this.params);
    this.maxLevel = maxLevelFor(body.radius, this.quality.resolution, this.quality.targetVertexSpacing);

    const permitted = new Set(body.def.biomes);
    const used: BiomeDef[] = BIOMES.filter((b) => permitted.has(b.id));
    this.biomeBands = used.map((b) => [
      b.id,
      { temperature: b.temperature, humidity: b.humidity, elevation: b.elevation, maxSlope: b.maxSlope },
    ]);
    this.biomeColors = used.map((b) => [b.id, { color: b.color, detailColor: b.detailColor }]);
    this.hasVegetation =
      used.some(
        (b) => (b.vegetationDensity > 0 || b.scatterDensity > 0) && (BIOME_VEGETATION[b.id]?.length ?? 0) > 0,
      ) || (permitted.has('ocean') && (UNDERWATER_VEGETATION.ocean?.length ?? 0) > 0);

    // Everything except the night-emissive terms, which the mesher has no use
    // for. Derived by exclusion rather than by listing the seven colours again,
    // so adding a band to the ramp reaches the worker without a second edit.
    const { nightEmissive: _n, nightEmissiveStrength: _ns, ...paletteColors } = body.def.palette;
    this.palette = paletteColors;

    for (const root of rootChunks()) this.ensureRecord(root);
  }

  /**
   * This body's scatter grid resolution, from its radius and the current
   * quality tier's cell size.
   *
   * A getter rather than a constructor-time constant because
   * `vegetationQuality` is reassigned live when the player changes quality or
   * toggles vegetation. Chunks already built keep the grid they were built
   * with until they are rebuilt, which is correct: a quality change is the one
   * legitimate reason for the forest to be re-rolled.
   */
  get vegetationCellsPerFaceEdge(): number {
    return vegetationCellsPerFaceEdge(this.body.radius, this.vegetationQuality.cellMetres, this.maxLevel);
  }

  get seaLevelRadius(): number {
    return this.params.seaLevel === Number.NEGATIVE_INFINITY
      ? -Infinity
      : this.body.radius + this.params.seaLevel;
  }

  get hasOcean(): boolean {
    return this.params.seaLevel !== Number.NEGATIVE_INFINITY;
  }

  /**
   * Distance, metres, within which every drawn chunk is guaranteed to carry
   * vegetation instances — the renderer's hard outer wall, which its fade has
   * to finish inside of.
   *
   * A chunk at level L stops being drawn once its *parent* stops splitting,
   * which happens at `splitFactor * size(L-1)`. The coarsest vegetated level
   * is `maxLevel - VEGETATION_LOD_DEPTH`, so that parent is one level coarser
   * again. `chunkSizeMetres` shrinks by up to `1/sqrt(3)` toward a cube-face
   * corner, and this has to hold for the *worst* chunk on the sphere rather
   * than the average one — a bound that only held at face centres would still
   * leave a hard, unfaded forest edge wherever the camera happened to be near
   * a corner.
   */
  get vegetationRange(): number {
    const coarsestVegetated = Math.max(0, this.maxLevel - VEGETATION_LOD_DEPTH);
    const parentLevel = Math.max(0, coarsestVegetated - 1);
    const smallestParent = chunkExtent(parentLevel) * this.body.radius * CUBE_FACE_MIN_DISTORTION;
    return smallestParent * this.quality.splitFactor;
  }

  /**
   * Updates the LOD tree.
   *
   * `observer` is the camera position in the body-fixed frame.
   * `velocity` is used to bias priority toward where the camera is heading, so
   * terrain is generated ahead of a fast-moving vessel rather than behind it.
   */
  update(observer: Vec3, velocity: Vec3, t: number): void {
    this.visibleKeys.clear();
    this.updateIndex++;
    const lookAhead = velocity.clone().mulScalar(3);
    const predicted = Vec3.addVectors(observer, lookAhead);

    for (const root of rootChunks()) {
      this.updateNode(root, observer, predicted, t);
    }

    this.evictStale(t);
  }

  private touch(record: ChunkRecord, t: number): ChunkRecord {
    record.lastSeen = t;
    record.lastTouch = this.updateIndex;
    return record;
  }

  private updateNode(id: ChunkId, observer: Vec3, predicted: Vec3, t: number): void {
    // Chunks on the far side of the planet are dropped before anything else is
    // decided about them: not marked visible, not built, and left free for
    // eviction. Testing here rather than at draw time means the far hemisphere
    // costs no memory and no worker time either.
    if (this.isHidden(id, observer)) return;

    const record = this.touch(this.ensureRecord(id), t);

    const size = record.size;
    const maxRadius = record.data ? record.data.maxRadius : this.body.radius + this.heightField.ceiling;
    const distance = distanceToChunk(id, observer, this.body.radius, maxRadius, this.scratch);
    record.distance = distance;

    const canSplit = id.level < this.maxLevel;
    const splitDistance = size * this.quality.splitFactor;
    const mergeDistance = size * this.quality.mergeFactor;

    if (canSplit && (record.split ? distance < mergeDistance : distance < splitDistance)) {
      const children = chunkChildren(id);
      let allReady = true;
      for (const child of children) {
        // A child over the horizon is never drawn, so waiting for it would hold
        // the parent at a coarse level for no reason.
        if (this.isHidden(child, observer)) continue;
        const cr = this.touch(this.ensureRecord(child), t);
        if (!cr.data) {
          allReady = false;
          this.requestBuild(cr, observer, predicted);
        }
      }

      if (allReady) {
        record.split = true;
        for (const child of children) this.updateNode(child, observer, predicted, t);
        return;
      }

      // Children are still loading: keep this node visible so no hole appears.
      record.split = false;
      this.markVisible(record, observer, predicted);
      return;
    }

    record.split = false;
    this.markVisible(record, observer, predicted);
  }

  /** Horizon test against the lowest terrain the body can have. */
  private isHidden(id: ChunkId, observer: Vec3): boolean {
    const occluder = this.body.radius + this.heightField.floor;
    const reach = this.body.radius + this.heightField.ceiling;
    return isBeyondHorizon(id, observer, occluder, reach, this.scratch);
  }

  private markVisible(record: ChunkRecord, observer: Vec3, predicted: Vec3): void {
    this.visibleKeys.add(record.key);
    if (!record.data) this.requestBuild(record, observer, predicted);
    if (record.id.level > this.maxLevelReached) this.maxLevelReached = record.id.level;
  }

  private ensureRecord(id: ChunkId): ChunkRecord {
    const key = chunkKey(id);
    let record = this.chunks.get(key);
    if (!record) {
      record = {
        id,
        key,
        data: null,
        pending: false,
        split: false,
        distance: Infinity,
        lastSeen: 0,
        lastTouch: -1,
        size: chunkSizeMetres(id, this.body.radius),
      };
      this.chunks.set(key, record);
    }
    return record;
  }

  private requestBuild(record: ChunkRecord, observer: Vec3, predicted: Vec3): void {
    if (record.pending || record.data) return;
    if (this.countPending() >= this.quality.maxPendingBuilds) return;

    // Priority combines current distance with predicted distance, so chunks the
    // camera is flying toward are built before chunks it is leaving behind.
    const maxRadius = this.body.radius + this.heightField.ceiling;
    const dNow = distanceToChunk(record.id, observer, this.body.radius, maxRadius, this.scratch);
    const dNext = distanceToChunk(record.id, predicted, this.body.radius, maxRadius, this.scratch);
    const priority = Math.min(dNow, dNext) + record.id.level * 1000;

    const request: ChunkBuildRequest = {
      key: record.key,
      id: record.id,
      params: this.params,
      resolution: this.quality.resolution,
      palette: this.palette,
      biomeBands: this.biomeBands,
      biomeColors: this.biomeColors,
    };

    // Only the finest four source levels carry vegetation. Each coarser source
    // persists roughly twice as far before its terrain parent merges,
    // extending the billboard forest without populating the whole planet.
    //
    // Every one of them scatters over the *same* planet-wide cell grid and
    // differs only in `cellStride` — a coarse chunk draws a deterministic
    // subset of the identical plants rather than a forest of its own. That is
    // what makes the four levels one forest seen at four detail levels instead
    // of four unrelated forests occupying the same ground in turn.
    if (
      this.hasVegetation &&
      this.vegetationQuality.enabled &&
      this.vegetationQuality.cellMetres > 0 &&
      record.id.level >= this.maxLevel - VEGETATION_LOD_DEPTH
    ) {
      const lodDelta = this.maxLevel - record.id.level;
      request.vegetation = {
        cellsPerFaceEdge: this.vegetationCellsPerFaceEdge,
        cellStride: vegetationCellStride(lodDelta),
        densityScale: this.vegetationQuality.densityScale,
        maxInstances: Math.max(1, Math.round(this.vegetationQuality.maxInstancesPerChunk)),
        // Only the finest source chunks can ever be close enough to render
        // sub-metre cover. Coarser source levels are for medium silhouettes
        // and distant cards, so carrying grass there wastes roughly half the
        // worker payload and GPU instance buffers.
        includeGroundCover: lodDelta === 0,
      };
    }

    record.pending = true;
    this.pool
      .submit(request, priority)
      .then((data) => {
        record.pending = false;
        // The record may have been evicted while the build was in flight.
        if (!this.chunks.has(record.key)) return;
        record.data = data;
        this.events.onChunkReady(record);
        // The listeners above have taken what they need — the renderer has
        // handed the vertex and index arrays to a `BufferAttribute`, and the
        // vegetation layer has read the chunk's centre and bounding radius.
        // Nothing consults them again, so the streamer stops being the second
        // thing keeping 312 KB per chunk alive. See `releaseChunkMeshArrays`.
        releaseChunkMeshArrays(data);
      })
      .catch((err) => {
        record.pending = false;
        if (err instanceof Error && err.message !== 'cancelled') {
          console.warn(`[PlanetSurface] chunk ${record.key} failed:`, err.message);
        }
      });
  }

  private countPending(): number {
    let n = 0;
    for (const r of this.chunks.values()) if (r.pending) n++;
    return n;
  }

  /**
   * Evicts chunks that have not been relevant recently, oldest and furthest
   * first, until the resident count is within budget.
   */
  private evictStale(t: number): void {
    const live = this.updateIndex;
    const evictable: ChunkRecord[] = [];
    let resident = 0;

    for (const r of this.chunks.values()) {
      if (r.data) resident++;
      // Roots anchor the tree and are never rebuilt; nodes this update walked
      // are load-bearing even when they are not drawn; a build in flight will
      // resolve into a record that no longer exists.
      if (r.id.level === 0 || r.lastTouch === live || r.pending) continue;

      if (!r.data) {
        // A stale record with no geometry costs nothing to recreate and would
        // otherwise accumulate for every chunk the camera has ever approached.
        this.chunks.delete(r.key);
        continue;
      }
      evictable.push(r);
    }

    let toRemove = resident - this.quality.maxChunks;
    if (toRemove <= 0) return;

    // Oldest first, then furthest: the chunk least likely to be wanted again
    // soon goes first.
    evictable.sort((a, b) => {
      const ageA = t - a.lastSeen;
      const ageB = t - b.lastSeen;
      if (ageA !== ageB) return ageB - ageA;
      return b.distance - a.distance;
    });

    for (const r of evictable) {
      if (toRemove <= 0) break;
      this.chunks.delete(r.key);
      this.events.onChunkRemoved(r.key);
      this.evictions++;
      toRemove--;
    }
  }

  /** Keys of the chunks that should currently be rendered. */
  get visible(): ReadonlySet<string> {
    return this.visibleKeys;
  }

  getChunk(key: string): ChunkRecord | undefined {
    return this.chunks.get(key);
  }

  /**
   * True when every chunk currently in `visible` actually has geometry —
   * there is nothing the tree wants drawn right now that is not there yet.
   *
   * This is deliberately not "do the six root records have geometry": a root
   * that has subdivided never gets its own geometry at all (see `updateNode`
   * — a split node recurses into its children without calling
   * `requestBuild` on itself), which is the ordinary state for any body the
   * camera is standing near. The "parent stays visible until every child is
   * ready" rule already prevents holes *within* an otherwise-streamed body,
   * but it says nothing about a body's very first chunks, which start out
   * visible and pending at the same time. Callers deciding whether to hide
   * this body's astronomical impostor in favour of its real terrain want
   * exactly this: is there currently nothing missing.
   *
   * Meant to be sampled once to latch a decision, not re-checked every frame
   * — ordinary streaming (new chunks needed as the camera moves) will make
   * this false again long after the body's first coverage was complete, and
   * that is not a hole, just detail catching up.
   */
  get fullyStreamed(): boolean {
    if (this.visibleKeys.size === 0) return false;
    for (const key of this.visibleKeys) {
      if (!this.chunks.get(key)?.data) return false;
    }
    return true;
  }

  /**
   * Drops every resident chunk's geometry and clears the height-field cache,
   * without touching the tree shape (which nodes are currently split) or the
   * chunk records themselves. For live-tuning the terrain profile from a
   * debug UI: existing geometry was built from the *old* parameter values,
   * but the *decision* of what should be visible at the camera's current
   * position is still correct and there is no reason to re-derive it from
   * the roots.
   *
   * Deliberately reuses the normal pending-build machinery rather than
   * building synchronously here — the next `update()` sees every record
   * missing `data` and re-requests it exactly the way a freshly streamed-in
   * chunk is requested, so the same "parent stays visible until every child
   * is ready" rule that prevents a hole while loading also covers regenerating.
   */
  invalidateAll(): void {
    for (const r of this.chunks.values()) {
      if (r.pending) this.pool.cancel(poolJobKey(this.params.planetId, r.key));
      if (r.data) this.events.onChunkRemoved(r.key);
      r.data = null;
      r.pending = false;
    }
    this.heightField.clearCache();
  }

  /**
   * Live-edits this body's terrain profile and regenerates every chunk from
   * it. Mutates `params.profile` in place (rather than swapping in a new
   * object) because both `HeightField` and every in-flight `ChunkBuildRequest`
   * hold onto this exact `TerrainParams`, not a copy.
   *
   * Most profile fields are read directly at generation time and need
   * nothing else, but `seaLevel` and the continental threshold/width are
   * derived once and cached on `params` — the same derivation
   * `terrainParamsFor` does at construction — so a change to
   * `seaLevelFraction`, `oceanCoverage`, `continentScale` or `warpStrength`
   * needs them recomputed too, or the edit would change the noise but not
   * where land actually ends up.
   */
  updateProfile(partial: Partial<TerrainProfile>): void {
    Object.assign(this.params.profile, partial);
    const p = this.params.profile;
    this.params.seaLevel =
      p.seaLevelFraction <= -1 ? Number.NEGATIVE_INFINITY : p.seaLevelFraction * p.maxElevation;
    const calibration = calibrateContinents(this.params);
    this.params.continentThreshold = calibration.threshold;
    this.params.continentWidth = calibration.width;
    this.invalidateAll();
  }

  /** Cancels all pending work and drops all geometry. */
  dispose(): void {
    for (const r of this.chunks.values()) {
      if (r.pending) this.pool.cancel(poolJobKey(this.params.planetId, r.key));
      if (r.data) this.events.onChunkRemoved(r.key);
    }
    this.chunks.clear();
    this.visibleKeys.clear();
    this.heightField.clearCache();
  }

  stats(): StreamingStats {
    const byLevel = new Map<number, number>();
    let resident = 0;
    let pending = 0;
    for (const r of this.chunks.values()) {
      if (r.pending) pending++;
      if (!r.data) continue;
      resident++;
      byLevel.set(r.id.level, (byLevel.get(r.id.level) ?? 0) + 1);
    }
    return {
      resident,
      pending,
      visible: this.visibleKeys.size,
      byLevel,
      evictions: this.evictions,
      maxLevelReached: this.maxLevelReached,
    };
  }
}
